<script setup>
import { ref, computed, watch, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useVuelidate } from '@vuelidate/core';
import { useAlert } from 'dashboard/composables';
import { required } from '@vuelidate/validators';
import { isPhoneE164OrEmpty } from 'shared/helpers/Validators';

import NextButton from 'dashboard/components-next/button/Button.vue';
import QRCodeModal from 'dashboard/components/QRCodeModal.vue';
import WhatsappWebGatewayApi from 'dashboard/api/whatsappWebGateway';
import WhatsappAdminApi from 'dashboard/api/whatsappAdminApi';

const props = defineProps({
  inbox: {
    type: Object,
    default: () => ({}),
  },
  isLoading: {
    type: Boolean,
    default: false,
  },
  mode: {
    type: String,
    default: 'create', // 'create' or 'edit'
    validator: value => ['create', 'edit'].includes(value),
  },
});

const emit = defineEmits(['submit']);

const { t } = useI18n();

const inboxName = ref('');
const phoneNumber = ref('');
const gatewayBaseUrl = ref('');
const basicAuthUser = ref('');
const basicAuthPassword = ref('');
const webhookSecret = ref('');
const includeSignature = ref(true);
const ignoreGroupMessages = ref(false);
const showQRModal = ref(false);
const connectionStatus = ref(null);
const isLoadingStatus = ref(false);
const isDisconnecting = ref(false);
const isReconnecting = ref(false);
const connectionMode = ref('provision'); // 'existing' or 'provision'
const adminApiConfigured = ref(false);
const isCheckingAdminApi = ref(false);
const isProvisioning = ref(false);
const provisionedPort = ref(null);
const provisionedWebhookSecret = ref(null);
const isProvisionedInstance = ref(false);
const isSyncing = ref(false);
const syncStatus = ref(null);
const syncStats = ref(null);
const lastSyncAt = ref(null);

const gatewayConfig = computed(() => ({
  gatewayBaseUrl: gatewayBaseUrl.value,
  phoneNumber: phoneNumber.value,
  basicAuthUser: basicAuthUser.value,
  basicAuthPassword: basicAuthPassword.value,
}));

const canShowQRCode = computed(() => {
  return gatewayBaseUrl.value && gatewayBaseUrl.value.trim() !== '';
});

const submitButtonLabel = computed(() => {
  return props.mode === 'create'
    ? t('INBOX_MGMT.ADD.WHATSAPP_WEB.SUBMIT_BUTTON')
    : t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_SECTION_UPDATE_BUTTON');
});

const showInboxNameField = computed(() => {
  return props.mode === 'create';
});

const connectionStatusText = computed(() => {
  if (isLoadingStatus.value) return 'CHECKING';
  if (!connectionStatus.value) return 'UNKNOWN';

  // Parse the status response based on QRCodeModal logic
  if (connectionStatus.value.code === 'SUCCESS') {
    // Check if logged in (similar to QRCodeModal logic)
    if (connectionStatus.value.results?.is_logged_in) {
      return 'CONNECTED';
    }

    // Check status string for more detailed info
    const status = connectionStatus.value.results?.status || '';
    if (status.includes('connected') || status.includes('authenticated')) {
      return 'CONNECTED';
    }
    if (
      status.includes('disconnected') ||
      status.includes('not authenticated')
    ) {
      return 'DISCONNECTED';
    }
    if (status.includes('qr') || status.includes('waiting')) {
      return 'WAITING_QR';
    }

    // Default to disconnected if success but no logged in flag
    return 'DISCONNECTED';
  }
  return 'ERROR';
});

const connectionStatusColor = computed(() => {
  switch (connectionStatusText.value) {
    case 'CONNECTED':
      return 'text-green-600 bg-green-50 border-green-200';
    case 'DISCONNECTED':
    case 'ERROR':
      return 'text-red-600 bg-red-50 border-red-200';
    case 'WAITING_QR':
      return 'text-yellow-600 bg-yellow-50 border-yellow-200';
    case 'CHECKING':
      return 'text-blue-600 bg-blue-50 border-blue-200';
    default:
      return 'text-gray-600 bg-gray-50 border-gray-200';
  }
});

const connectionStatusLabel = computed(() => {
  const statusKey = connectionStatusText.value;
  const translationKey = `INBOX_MGMT.ADD.WHATSAPP_WEB.CONNECTION_STATUS.${statusKey}`;
  return t(translationKey);
});

const showConnectButton = computed(() => {
  return connectionStatusText.value !== 'CONNECTED';
});

const showDisconnectButton = computed(() => {
  return connectionStatusText.value === 'CONNECTED';
});

const showReconnectButton = computed(() => {
  // Reconnect should be available when connected (to restart connection)
  // or when there are connection issues
  return (
    connectionStatusText.value === 'CONNECTED' ||
    connectionStatusText.value === 'DISCONNECTED' ||
    connectionStatusText.value === 'ERROR'
  );
});

const showSyncButton = computed(() => {
  return connectionStatusText.value === 'CONNECTED';
});

const syncStatusText = computed(() => {
  if (isSyncing.value) return 'IN_PROGRESS';
  if (!syncStatus.value || syncStatus.value === 'not_started') return 'NEVER';
  return syncStatus.value.toUpperCase();
});

const syncStatusColor = computed(() => {
  switch (syncStatusText.value) {
    case 'COMPLETED':
      return 'text-violet-600 bg-violet-50 border-violet-200 dark:text-violet-400 dark:bg-violet-900/20 dark:border-violet-800';
    case 'IN_PROGRESS':
      return 'text-blue-600 bg-blue-50 border-blue-200 dark:text-blue-400 dark:bg-blue-900/20 dark:border-blue-800';
    case 'FAILED':
      return 'text-red-600 bg-red-50 border-red-200 dark:text-red-400 dark:bg-red-900/20 dark:border-red-800';
    default:
      return 'text-slate-500 bg-slate-50 border-slate-200 dark:text-slate-400 dark:bg-slate-800/50 dark:border-slate-700';
  }
});

const syncStatusLabel = computed(() => {
  const statusKey = syncStatusText.value;
  return t(`INBOX_MGMT.ADD.WHATSAPP_WEB.HISTORY_SYNC.STATUS.${statusKey}`);
});

const showConnectionModeToggle = computed(() => {
  return props.mode === 'create' && adminApiConfigured.value;
});

const isProvisioningMode = computed(() => {
  // In edit mode, always show gateway fields so users can update them
  if (props.mode === 'edit') {
    return false;
  }
  // Only use provisioning mode if admin API is available and user selected provision
  return adminApiConfigured.value && connectionMode.value === 'provision';
});

const validationRules = computed(() => {
  const baseValidations = {
    phoneNumber: { required, isPhoneE164OrEmpty },
  };

  // In provisioning mode, we don't need gateway URL, basic auth, or webhook secret
  if (!isProvisioningMode.value) {
    baseValidations.gatewayBaseUrl = { required };
    baseValidations.basicAuthUser = {};
    baseValidations.basicAuthPassword = {};
    baseValidations.webhookSecret = { required };
  }

  if (props.mode === 'create') {
    baseValidations.inboxName = { required };
  }

  return baseValidations;
});

const v$ = useVuelidate(validationRules, {
  inboxName,
  phoneNumber,
  gatewayBaseUrl,
  basicAuthUser,
  basicAuthPassword,
  webhookSecret,
});

const setDefaults = inbox => {
  if (!inbox) return;

  inboxName.value = inbox.name || '';
  phoneNumber.value = inbox.phone_number || '';

  if (inbox.provider_config) {
    gatewayBaseUrl.value = inbox.provider_config.gateway_base_url || '';
    basicAuthUser.value = inbox.provider_config.basic_auth_user || '';
    basicAuthPassword.value = inbox.provider_config.basic_auth_password || '';
    webhookSecret.value = inbox.provider_config.webhook_secret || '';
    includeSignature.value = inbox.provider_config.include_signature !== false;
    ignoreGroupMessages.value =
      inbox.provider_config.ignore_group_messages === true;

    // Load sync status from provider config as initial values
    // The actual current status will be fetched via checkSyncStatus()
    syncStatus.value = inbox.provider_config.history_sync_status || null;
    lastSyncAt.value = inbox.provider_config.history_sync_at || null;
    syncStats.value = inbox.provider_config.history_sync_stats || null;
  }
};

const openQRModal = () => {
  if (!canShowQRCode.value) {
    useAlert(t('INBOX_MGMT.ADD.WHATSAPP_WEB.TEST_CONNECTION.VALIDATION_ERROR'));
    return;
  }
  showQRModal.value = true;
};

const closeQRModal = () => {
  showQRModal.value = false;
};

const checkConnectionStatus = async () => {
  if (props.mode !== 'edit' || !props.inbox?.id) return;

  isLoadingStatus.value = true;
  try {
    const response = await WhatsappWebGatewayApi.getStatus(props.inbox.id);
    connectionStatus.value = response.data.data;
  } catch (error) {
    // Connection status check failed
    connectionStatus.value = { code: 'ERROR', error: error.message };
  } finally {
    isLoadingStatus.value = false;
  }
};

const checkAdminApiStatus = async () => {
  isCheckingAdminApi.value = true;
  try {
    const response = await WhatsappAdminApi.checkAdminApiStatus();
    adminApiConfigured.value =
      response.data.configured && response.data.healthy;
  } catch (error) {
    adminApiConfigured.value = false;
  } finally {
    isCheckingAdminApi.value = false;
  }
};

const handleSubmit = async () => {
  v$.value.$touch();
  if (v$.value.$invalid) {
    return;
  }

  // If provisioning mode, provision the instance first
  if (isProvisioningMode.value) {
    isProvisioning.value = true;
    try {
      const provisionResponse = await WhatsappAdminApi.provisionInstance(
        phoneNumber.value
      );

      // Response is { success: true, data: { gateway_base_url, port, webhook_secret, ... } }
      const provisionedData = provisionResponse.data.data;
      const {
        gateway_base_url,
        basic_auth_user,
        basic_auth_password,
        port,
        webhook_secret,
      } = provisionedData;

      // Use the provisioned credentials
      gatewayBaseUrl.value = gateway_base_url;
      basicAuthUser.value = basic_auth_user;
      basicAuthPassword.value = basic_auth_password;
      // Store provisioning info for teardown later
      provisionedPort.value = port;
      provisionedWebhookSecret.value = webhook_secret;
      isProvisionedInstance.value = true;

      useAlert(t('INBOX_MGMT.ADD.WHATSAPP_WEB.PROVISIONING.SUCCESS'));
    } catch (error) {
      isProvisioning.value = false;
      const errorMessage = error.response?.data?.error || error.message;

      if (errorMessage.includes('No available ports')) {
        useAlert(t('INBOX_MGMT.ADD.WHATSAPP_WEB.PROVISIONING.NO_PORTS'));
      } else {
        useAlert(
          errorMessage || t('INBOX_MGMT.ADD.WHATSAPP_WEB.PROVISIONING.ERROR')
        );
      }
      return;
    } finally {
      isProvisioning.value = false;
    }
  }

  // Build provider_config, omitting optional Basic Auth if left blank
  // Preserve existing sync status fields that shouldn't be overwritten by form submission
  const existingConfig = props.inbox?.provider_config || {};
  const providerConfig = {
    gateway_base_url: gatewayBaseUrl.value,
    include_signature: includeSignature.value,
    ignore_group_messages: ignoreGroupMessages.value,
    // Preserve sync-related fields
    history_sync_status: existingConfig.history_sync_status,
    history_sync_at: existingConfig.history_sync_at,
    history_sync_stats: existingConfig.history_sync_stats,
  };

  if (basicAuthUser.value && basicAuthPassword.value) {
    providerConfig.basic_auth_user = basicAuthUser.value;
    providerConfig.basic_auth_password = basicAuthPassword.value;
  }

  // Mark as provisioned instance so teardown service can clean up
  if (isProvisionedInstance.value) {
    providerConfig.provisioned = true;
    providerConfig.instance_port = provisionedPort.value;
    providerConfig.webhook_secret = provisionedWebhookSecret.value;
  } else {
    // For existing instances, use the user-provided webhook secret
    providerConfig.webhook_secret = webhookSecret.value;
  }

  const formData = {
    name: inboxName.value,
    phone_number: phoneNumber.value,
    provider_config: providerConfig,
  };

  emit('submit', formData);
};

const refreshConnectionStatus = async () => {
  await checkConnectionStatus();
};

const disconnectWhatsApp = async () => {
  if (!props.inbox?.id) return;

  // Show confirmation dialog
  const confirmed = window.confirm(
    t('INBOX_MGMT.ADD.WHATSAPP_WEB.DISCONNECT.CONFIRMATION')
  );

  if (!confirmed) return;

  isDisconnecting.value = true;
  try {
    await WhatsappWebGatewayApi.logout(props.inbox.id);
    useAlert(t('INBOX_MGMT.ADD.WHATSAPP_WEB.DISCONNECT.SUCCESS'));
    // Refresh status after successful disconnect
    await checkConnectionStatus();
  } catch (error) {
    useAlert(
      error.message || t('INBOX_MGMT.ADD.WHATSAPP_WEB.DISCONNECT.ERROR')
    );
  } finally {
    isDisconnecting.value = false;
  }
};

const reconnectWhatsApp = async () => {
  if (!props.inbox?.id) return;

  isReconnecting.value = true;
  try {
    await WhatsappWebGatewayApi.reconnect(props.inbox.id);
    useAlert(t('INBOX_MGMT.ADD.WHATSAPP_WEB.RECONNECT.SUCCESS'));
    // Refresh status after successful reconnect
    await checkConnectionStatus();
  } catch (error) {
    useAlert(error.message || t('INBOX_MGMT.ADD.WHATSAPP_WEB.RECONNECT.ERROR'));
  } finally {
    isReconnecting.value = false;
  }
};

const pollSyncStatus = async () => {
  if (!props.inbox?.id) return;

  try {
    const { data } = await WhatsappWebGatewayApi.getSyncStatus(props.inbox.id);
    syncStatus.value = data.status;
    lastSyncAt.value = data.synced_at;
    syncStats.value = data.stats;

    // Continue polling if still in progress
    if (data.status === 'in_progress') {
      setTimeout(pollSyncStatus, 2000);
    } else {
      isSyncing.value = false;
      if (data.status === 'completed') {
        const imported = data.stats?.messages_imported || 0;
        useAlert(
          t('INBOX_MGMT.ADD.WHATSAPP_WEB.HISTORY_SYNC.COMPLETED', {
            count: imported,
          })
        );
      }
    }
  } catch {
    isSyncing.value = false;
  }
};

const checkSyncStatus = async () => {
  if (props.mode !== 'edit' || !props.inbox?.id) return;

  try {
    const { data } = await WhatsappWebGatewayApi.getSyncStatus(props.inbox.id);
    syncStatus.value = data.status;
    lastSyncAt.value = data.synced_at;
    syncStats.value = data.stats;

    // Only start polling if sync is genuinely in progress
    if (data.status === 'in_progress') {
      isSyncing.value = true;
      setTimeout(pollSyncStatus, 2000);
    }
  } catch {
    // Sync status check failed, use values from inbox prop
  }
};

const handleWhatsAppConnected = async () => {
  closeQRModal();
  // Refresh the connection status after successful connection
  await checkConnectionStatus();

  // Trigger history sync when WhatsApp connects
  if (props.inbox?.id) {
    try {
      isSyncing.value = true;
      syncStatus.value = 'in_progress';
      await WhatsappWebGatewayApi.syncHistory(props.inbox.id);
      useAlert(t('INBOX_MGMT.ADD.WHATSAPP_WEB.HISTORY_SYNC.STARTED'));
      // Start polling for sync completion
      setTimeout(pollSyncStatus, 2000);
    } catch {
      // History sync is non-critical, silently fail
      isSyncing.value = false;
      syncStatus.value = null;
    }
  }
};

const syncHistory = async () => {
  if (!props.inbox?.id) return;

  isSyncing.value = true;
  syncStatus.value = 'in_progress';
  try {
    await WhatsappWebGatewayApi.syncHistory(props.inbox.id);
    useAlert(t('INBOX_MGMT.ADD.WHATSAPP_WEB.HISTORY_SYNC.STARTED'));
    // Start polling for completion
    setTimeout(pollSyncStatus, 2000);
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('History sync failed:', error);
    syncStatus.value = 'failed';
    isSyncing.value = false;
    useAlert(t('INBOX_MGMT.ADD.WHATSAPP_WEB.HISTORY_SYNC.ERROR'));
  }
};

watch(
  () => props.inbox,
  newInbox => {
    if (newInbox && props.mode === 'edit') {
      setDefaults(newInbox);
      checkConnectionStatus();
      checkSyncStatus();
    }
  },
  { immediate: true }
);

onMounted(() => {
  if (props.mode === 'create') {
    checkAdminApiStatus();
  }
});
</script>

<template>
  <form class="flex flex-wrap flex-col mx-0" @submit.prevent="handleSubmit()">
    <!-- Connection Mode Toggle -->
    <div
      v-if="showConnectionModeToggle"
      class="flex-shrink-0 flex-grow-0 mb-4 p-4 border border-slate-200 dark:border-slate-700 rounded-lg"
    >
      <label
        class="block text-sm font-medium text-slate-900 dark:text-slate-100 mb-3"
      >
        {{ $t('INBOX_MGMT.ADD.WHATSAPP_WEB.CONNECTION_MODE.LABEL') }}
      </label>
      <div class="grid grid-cols-1 gap-3">
        <label
          class="flex items-start p-3 border rounded-lg cursor-pointer transition-colors"
          :class="
            connectionMode === 'provision'
              ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
              : 'border-slate-200 dark:border-slate-700 hover:border-slate-300'
          "
        >
          <input
            v-model="connectionMode"
            type="radio"
            value="provision"
            class="mt-1 mr-3"
          />
          <div>
            <div class="font-medium text-slate-900 dark:text-slate-100">
              {{ $t('INBOX_MGMT.ADD.WHATSAPP_WEB.CONNECTION_MODE.PROVISION') }}
            </div>
            <div class="text-xs text-slate-600 dark:text-slate-400 mt-1">
              {{
                $t('INBOX_MGMT.ADD.WHATSAPP_WEB.CONNECTION_MODE.PROVISION_DESC')
              }}
            </div>
          </div>
        </label>
        <label
          class="flex items-start p-3 border rounded-lg cursor-pointer transition-colors"
          :class="
            connectionMode === 'existing'
              ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
              : 'border-slate-200 dark:border-slate-700 hover:border-slate-300'
          "
        >
          <input
            v-model="connectionMode"
            type="radio"
            value="existing"
            class="mt-1 mr-3"
          />
          <div>
            <div class="font-medium text-slate-900 dark:text-slate-100">
              {{ $t('INBOX_MGMT.ADD.WHATSAPP_WEB.CONNECTION_MODE.EXISTING') }}
            </div>
            <div class="text-xs text-slate-600 dark:text-slate-400 mt-1">
              {{
                $t('INBOX_MGMT.ADD.WHATSAPP_WEB.CONNECTION_MODE.EXISTING_DESC')
              }}
            </div>
          </div>
        </label>
      </div>
    </div>

    <div v-if="showInboxNameField" class="flex-shrink-0 flex-grow-0">
      <label :class="{ error: v$.inboxName.$error }">
        {{ $t('INBOX_MGMT.ADD.WHATSAPP_WEB.INBOX_NAME.LABEL') }}
        <input
          v-model="inboxName"
          type="text"
          :placeholder="
            $t('INBOX_MGMT.ADD.WHATSAPP_WEB.INBOX_NAME.PLACEHOLDER')
          "
          @blur="v$.inboxName.$touch"
        />
        <span v-if="v$.inboxName.$error" class="message">
          {{ $t('INBOX_MGMT.ADD.WHATSAPP_WEB.INBOX_NAME.ERROR') }}
        </span>
      </label>
    </div>

    <div class="flex-shrink-0 flex-grow-0">
      <label :class="{ error: v$.phoneNumber.$error }">
        {{ $t('INBOX_MGMT.ADD.WHATSAPP_WEB.PHONE_NUMBER.LABEL') }}
        <input
          v-model="phoneNumber"
          type="text"
          :placeholder="
            $t('INBOX_MGMT.ADD.WHATSAPP_WEB.PHONE_NUMBER.PLACEHOLDER')
          "
          @blur="v$.phoneNumber.$touch"
        />
        <span v-if="v$.phoneNumber.$error" class="message">
          {{ $t('INBOX_MGMT.ADD.WHATSAPP_WEB.PHONE_NUMBER.ERROR') }}
        </span>
      </label>
    </div>

    <!-- Only show gateway fields in existing mode -->
    <div v-if="!isProvisioningMode" class="flex-shrink-0 flex-grow-0">
      <label :class="{ error: v$.gatewayBaseUrl.$error }">
        <span>
          {{ $t('INBOX_MGMT.ADD.WHATSAPP_WEB.GATEWAY_BASE_URL.LABEL') }}
          <span class="text-xs text-slate-11 ml-1">
            {{ $t('INBOX_MGMT.ADD.WHATSAPP_WEB.GATEWAY_BASE_URL.TOOLTIP') }}
          </span>
        </span>
        <input
          v-model="gatewayBaseUrl"
          type="url"
          :placeholder="
            $t('INBOX_MGMT.ADD.WHATSAPP_WEB.GATEWAY_BASE_URL.PLACEHOLDER')
          "
          @blur="v$.gatewayBaseUrl.$touch"
        />
        <span v-if="v$.gatewayBaseUrl.$error" class="message">
          {{ $t('INBOX_MGMT.ADD.WHATSAPP_WEB.GATEWAY_BASE_URL.ERROR') }}
        </span>
      </label>
    </div>

    <div v-if="!isProvisioningMode" class="grid grid-cols-2 gap-4">
      <div class="flex-shrink-0 flex-grow-0">
        <label :class="{ error: v$.basicAuthUser.$error }">
          {{ $t('INBOX_MGMT.ADD.WHATSAPP_WEB.BASIC_AUTH_USER.LABEL') }}
          <input
            v-model="basicAuthUser"
            type="text"
            :placeholder="
              $t('INBOX_MGMT.ADD.WHATSAPP_WEB.BASIC_AUTH_USER.PLACEHOLDER')
            "
            @blur="v$.basicAuthUser.$touch"
          />
          <span v-if="v$.basicAuthUser.$error" class="message">
            {{ $t('INBOX_MGMT.ADD.WHATSAPP_WEB.BASIC_AUTH_USER.ERROR') }}
          </span>
        </label>
      </div>

      <div class="flex-shrink-0 flex-grow-0">
        <label :class="{ error: v$.basicAuthPassword.$error }">
          {{ $t('INBOX_MGMT.ADD.WHATSAPP_WEB.BASIC_AUTH_PASSWORD.LABEL') }}
          <input
            v-model="basicAuthPassword"
            type="password"
            :placeholder="
              $t('INBOX_MGMT.ADD.WHATSAPP_WEB.BASIC_AUTH_PASSWORD.PLACEHOLDER')
            "
            @blur="v$.basicAuthPassword.$touch"
          />
          <span v-if="v$.basicAuthPassword.$error" class="message">
            {{ $t('INBOX_MGMT.ADD.WHATSAPP_WEB.BASIC_AUTH_PASSWORD.ERROR') }}
          </span>
        </label>
      </div>
    </div>

    <div v-if="!isProvisioningMode" class="flex-shrink-0 flex-grow-0">
      <label :class="{ error: v$.webhookSecret.$error }">
        <span>
          {{ $t('INBOX_MGMT.ADD.WHATSAPP_WEB.WEBHOOK_SECRET.LABEL') }}
          <span class="text-xs text-slate-11 ml-1">
            {{ $t('INBOX_MGMT.ADD.WHATSAPP_WEB.WEBHOOK_SECRET.TOOLTIP') }}
          </span>
        </span>
        <input
          v-model="webhookSecret"
          type="password"
          :placeholder="
            $t('INBOX_MGMT.ADD.WHATSAPP_WEB.WEBHOOK_SECRET.PLACEHOLDER')
          "
          @blur="v$.webhookSecret.$touch"
        />
        <span v-if="v$.webhookSecret.$error" class="message">
          {{ $t('INBOX_MGMT.ADD.WHATSAPP_WEB.WEBHOOK_SECRET.ERROR') }}
        </span>
      </label>
    </div>

    <div class="flex-shrink-0 flex-grow-0">
      <label class="flex items-center">
        <input v-model="includeSignature" type="checkbox" class="mr-2" />
        <span>
          {{ $t('INBOX_MGMT.ADD.WHATSAPP_WEB.INCLUDE_SIGNATURE.LABEL') }}
        </span>
      </label>
      <p class="text-xs text-slate-11 mt-1">
        {{ $t('INBOX_MGMT.ADD.WHATSAPP_WEB.INCLUDE_SIGNATURE.HELP_TEXT') }}
      </p>
    </div>

    <div class="flex-shrink-0 flex-grow-0">
      <label class="flex items-center">
        <input v-model="ignoreGroupMessages" type="checkbox" class="mr-2" />
        <span>
          {{ $t('INBOX_MGMT.ADD.WHATSAPP_WEB.IGNORE_GROUP_MESSAGES.LABEL') }}
        </span>
      </label>
      <p class="text-xs text-slate-11 mt-1">
        {{ $t('INBOX_MGMT.ADD.WHATSAPP_WEB.IGNORE_GROUP_MESSAGES.HELP_TEXT') }}
      </p>
    </div>

    <!-- Connection Status (only in edit mode) -->
    <div v-if="mode === 'edit'" class="flex-shrink-0 flex-grow-0 my-4">
      <div class="flex items-center justify-between mb-2">
        <span class="text-sm font-medium text-slate-12">
          {{ $t('INBOX_MGMT.ADD.WHATSAPP_WEB.CONNECTION_STATUS.LABEL') }}
        </span>
        <button
          type="button"
          :disabled="isLoadingStatus"
          class="text-sm text-blue-600 hover:text-blue-800 disabled:text-gray-400"
          @click="refreshConnectionStatus"
        >
          {{
            isLoadingStatus
              ? $t('INBOX_MGMT.ADD.WHATSAPP_WEB.CONNECTION_STATUS.REFRESHING')
              : $t('INBOX_MGMT.ADD.WHATSAPP_WEB.CONNECTION_STATUS.REFRESH')
          }}
        </button>
      </div>
      <div
        class="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium border"
        :class="connectionStatusColor"
      >
        <span
          v-if="isLoadingStatus"
          class="inline-block w-3 h-3 mr-2 border-2 border-current border-t-transparent rounded-full animate-spin"
        />
        <span v-else class="w-2 h-2 mr-2 rounded-full bg-current" />
        {{ connectionStatusLabel }}
      </div>
    </div>

    <!-- Message Sync Status (only in edit mode when connected) -->
    <div
      v-if="mode === 'edit' && showSyncButton"
      class="flex-shrink-0 flex-grow-0 mb-4"
    >
      <div class="flex items-center mb-2">
        <span class="text-sm font-medium text-slate-12">
          {{ $t('INBOX_MGMT.ADD.WHATSAPP_WEB.HISTORY_SYNC.STATUS.LABEL') }}
        </span>
      </div>
      <div class="flex items-center gap-3">
        <div
          class="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium border"
          :class="syncStatusColor"
        >
          <span
            v-if="isSyncing"
            class="inline-block w-3 h-3 mr-2 border-2 border-current border-t-transparent rounded-full animate-spin"
          />
          <span v-else class="w-2 h-2 mr-2 rounded-full bg-current" />
          {{ syncStatusLabel }}
        </div>
        <span
          v-if="syncStats?.messages_imported"
          class="text-xs text-slate-500 dark:text-slate-400"
        >
          {{ syncStats.messages_imported }}
          {{ $t('INBOX_MGMT.ADD.WHATSAPP_WEB.HISTORY_SYNC.STATS.MESSAGES') }}
        </span>
      </div>
      <p
        v-if="lastSyncAt"
        class="text-xs text-slate-500 dark:text-slate-400 mt-1"
      >
        {{ $t('INBOX_MGMT.ADD.WHATSAPP_WEB.HISTORY_SYNC.LAST_SYNC') }}
        {{ new Date(lastSyncAt).toLocaleString() }}
      </p>
    </div>

    <div class="flex gap-2 mt-4">
      <!-- Connect Button (QR Code) -->
      <NextButton
        v-if="mode === 'edit' && showConnectButton"
        type="button"
        color="green"
        variant="outline"
        size="md"
        icon="i-lucide-qr-code"
        :label="$t('INBOX_MGMT.ADD.WHATSAPP_WEB.QR_CODE.TITLE')"
        :disabled="!canShowQRCode"
        @click="openQRModal"
      />

      <!-- Disconnect Button -->
      <NextButton
        v-if="mode === 'edit' && showDisconnectButton"
        type="button"
        color="red"
        variant="outline"
        size="md"
        icon="i-lucide-log-out"
        :label="$t('INBOX_MGMT.ADD.WHATSAPP_WEB.DISCONNECT.TITLE')"
        :is-loading="isDisconnecting"
        @click="disconnectWhatsApp"
      />

      <!-- Reconnect Button -->
      <NextButton
        v-if="mode === 'edit' && showReconnectButton"
        type="button"
        color="orange"
        variant="outline"
        size="md"
        icon="i-lucide-refresh-cw"
        :label="$t('INBOX_MGMT.ADD.WHATSAPP_WEB.RECONNECT.TITLE')"
        :is-loading="isReconnecting"
        @click="reconnectWhatsApp"
      />

      <!-- Sync History Button -->
      <NextButton
        v-if="mode === 'edit' && showSyncButton"
        type="button"
        color="slate"
        variant="outline"
        size="md"
        icon="i-lucide-download"
        :label="$t('INBOX_MGMT.ADD.WHATSAPP_WEB.HISTORY_SYNC.BUTTON')"
        :is-loading="isSyncing"
        @click="syncHistory"
      />

      <!-- Submit Button -->
      <NextButton
        :is-loading="isLoading || isProvisioning"
        type="submit"
        variant="solid"
        color="blue"
        size="md"
        :label="
          isProvisioning
            ? $t('INBOX_MGMT.ADD.WHATSAPP_WEB.PROVISIONING.IN_PROGRESS')
            : submitButtonLabel
        "
      />
    </div>

    <!-- QR Code Modal -->
    <QRCodeModal
      :show="showQRModal"
      :gateway-config="gatewayConfig"
      :inbox-id="inbox?.id"
      @close="closeQRModal"
      @connected="handleWhatsAppConnected"
    />
  </form>
</template>
